import PropTypes from 'prop-types';
import { useContext, useEffect, useRef } from 'react';
import { PriceContext } from '../contexts/PriceContext';
import { UserContext } from '../contexts/UserContext';
import { useUnits } from '../hooks/useUnits';
import { gtmEventChangeUnits, gtmEventShowFeatures } from '../utils/analytics';
import { getCostsByPlanAndUnits, getLocalizedPrice } from '../utils/costs';
import { getUnitSelectOptions } from '../utils/plan';
import Plan from './Plan';

export default function Overview({ id, data, featurePageUrl, getLabel, onPlanSelect, paymentInterval, showPricePerUnit }) {
	const { useDecimalPlaces } = useContext(PriceContext);
	const { currency, locale } = useContext(UserContext);
	const [units, setUnits] = useUnits(data.units);
	const prevUnits = useRef(units);

	useEffect(() => {
		if (prevUnits.current !== units) {
			gtmEventChangeUnits({ plan: id, units });
		}
		prevUnits.current = units;
	}, [units]);

	const prices = getCostsByPlanAndUnits(data, units, paymentInterval, useDecimalPlaces);

	let paymentHintLabel = getLabel('payment_hint_monthly');
	if (paymentInterval === 'annually') {
		const annualPrice = getLocalizedPrice(prices.annually.total, currency, locale);
		paymentHintLabel = getLabel('payment_hint_annually').replace('%s', annualPrice);
	}

	const getPricePerUnit = () => {
		if (showPricePerUnit) {
			const price = getLocalizedPrice(prices.monthly.perUnit, currency, locale);
			return getLabel('plan_price_per_unit').replace('%s', price);
		} else {
			return null;
		}
	};

	const unitSelect = {
		label: getLabel('plan_unit_selection'),
		onChange: (units) => setUnits(parseInt(units)),
		options: getUnitSelectOptions(data),
		selected: units,
	};

	return (
		<Plan
			bottomLink={{ href: featurePageUrl, label: getLabel('plan_all_features'), onClick: () => gtmEventShowFeatures(id) }}
			buttonLabel={getLabel('plan_primary_button')}
			description={data.description}
			featureHeading={getLabel('plan_feature_list_heading')}
			featureListItems={data.features}
			highlight={data.most_popular ? getLabel('plan_most_popular') : ''}
			onButtonClick={() => onPlanSelect(id, data, paymentInterval, units)}
			price={getLocalizedPrice(prices.monthly.total, currency, locale)}
			priceExplanation={paymentHintLabel}
			priceInterval={`/ ${getLabel('payment_month')}`}
			pricePerUnit={getPricePerUnit()}
			unitSelect={unitSelect}
		/>
	);
}

Overview.propTypes = {
	data: PropTypes.object.isRequired,
	featurePageUrl: PropTypes.string,
	getLabel: PropTypes.func.isRequired,
	id: PropTypes.string.isRequired,
	onPlanSelect: PropTypes.func.isRequired,
	paymentInterval: PropTypes.string.isRequired,
	showPricePerUnit: PropTypes.bool.isRequired,
};
